import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {Subscription} from "rxjs";
import {RoutesViewSettingsRoutesModel} from "../../../models/routes-view-settings.model";
import {RoutePanelModel} from "../../../models/route-panel.model";
import {RoutesComponentStore, VmRoutesComponentState} from "../../state/routes.store";
import {RoutesQueryModel} from "../../../models/routes-query.model";
import {RouteStatusEnum} from "../../../enums/route-status.enum";
import {RouteModel} from "../../../models/route.model";
import {SortType} from "../../../../commons/types/sort.type";
import {TaskPurposeType} from "../../../../commons/types/task-purpose.type";
import {TaskType} from "../../../../tasks/types/task.type";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {RoutesDragAndDropService} from "../../services/routes-drag-and-drop.service";
import {DEFAULT_ROUTES_COLUMN_WIDTHS} from "../../../constants/routes-column-widths.constant";
import {ROUTES_COLUMN_GROUPS} from "../../../constants/routes-column-groups.constant";

require('./routes-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-routes-table-list',
  templateUrl: './routes-table-list.html',
  providers: [RoutesComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutesTableListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() settings: RoutesViewSettingsRoutesModel;
  @Input() panel: RoutePanelModel;
  @Input() panels: RoutePanelModel[];
  @Input() resourcesDropListIds: string[];
  @Input() routesDropListIds?: string[];
  @Input() queryString?: string;
  @Input() ids?: string[];
  @Input() from?: string;
  @Input() to?: string;
  @Input() status?: RouteStatusEnum;
  @Input() branchIds?: string[];
  @Input() depotIds?: string[];
  @Input() areaIds?: string[];
  @Input() agentIds?: string[];
  @Input() agentSkills?: string[];
  @Input() resourceIds?: string[];
  @Input() agentScheduleIds?: string[];
  @Input() tags?: string[];
  @Input() resourceTags?: string[];
  @Input() vehicleTypeIds?: string[];
  @Input() relationTypes?: string[];
  @Input() selectedIds: string[];
  @Input() excludeMode: boolean;
  @Input() visibleIds: string[];
  @Input() sortBy: any;
  @Input() sortType: SortType;
  @Input() selectedServiceIds: string[] = [];
  @Input() expandedRoutes: any[] = [];
  @Input() isResourceView: boolean = false;
  @Input() summaryContainer: HTMLElement;
  @Input() selectedRouteId?: string;

  @Output() onToggleSelectRoute = new EventEmitter<{ route: RouteModel, isSelected: boolean }>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() fetchNextEvent = new EventEmitter<null>();
  @Output() onToggleVisible = new EventEmitter<any>();
  @Output() onSelectRouteResource = new EventEmitter<any>();
  @Output() onLockRouteChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onDrillDown = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onEditRoutes = new EventEmitter<any>();
  @Output() onRoutesDropListIdsChange = new EventEmitter<{panel: RoutePanelModel, ids: string[]}>();
  @Output() onRoutesDropped = new EventEmitter<CdkDragDrop<any>>();
  @Output() onSettingsChange = new EventEmitter<RoutesViewSettingsRoutesModel>();

  get uniqueId(): string {
    return 'routes-' + this.panel?.id;
  }

  public defaultWidths = DEFAULT_ROUTES_COLUMN_WIDTHS;
  public columnGroups = ROUTES_COLUMN_GROUPS;

  @ViewChild('headerContainer', {static: false}) public headerContainer: ElementRef;
  @ViewChild('bodyContainer', {read: ElementRef, static: false}) public bodyContainer: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport;

  public vm: VmRoutesComponentState;
  public selectedKeys: { [key: string]: boolean } = {};
  public visibleKeys: { [key: string]: boolean } = {};
  public allPanelIds: string[];
  public TaskPurposeType = TaskPurposeType;

  private readonly loadLimit: number = 20;
  private vmSubscription: Subscription;
  private clickTimeout: any | null = null;
  private resizeObserver: ResizeObserver;
  private scrollEndTimer: any | null = null;
  private lastScrollTop: number = 0;

  constructor(
    @Inject(RoutesComponentStore) private routesStore: RoutesComponentStore,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject('splitService') private splitService,
    @Inject('$timeout') private $timeout,
    @Inject(RoutesDragAndDropService) private routesDragAndDropService: RoutesDragAndDropService
  ) {
  }

  onDrop(route: any, event: CdkDragDrop<string[]>) {
    this.routesDragAndDropService.onDropRouteList(
      route,
      event,
      this.selectedServiceIds,
      this.expandedRoutes
    ).then((result) => {
      if (result) {
        this.onEditRoutes.emit(result.data);
      }
    });
  }

  ngOnInit() {
    this.cdkEnterPredicate = this.cdkEnterPredicate.bind(this);

    this.vmSubscription = this.routesStore.vm$.subscribe((vm) => {
      if (!_.isEqual(vm.routes, this.vm?.routes)) {
        const ids = _.map(vm.routes, (r) => {
          return `${this.panel.id}-${r.id}`;
        });
        this.onRoutesDropListIdsChange.emit({panel: this.panel, ids: ids});
      }

      this.vm = {...vm};
      this.cdr.detectChanges();

      setTimeout(() => {
        if (this.viewport && !this.vm.loading && !!this.vm.cursor) {
          this.viewport.checkViewportSize();
          const nextBatchThreshold = this.viewport.getViewportSize();
          const bottomOffset = this.viewport.measureScrollOffset('bottom');

          if (bottomOffset < nextBatchThreshold) {
            this._getRoutes();
          }
        }
      }, 0);
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.viewport?.checkViewportSize();
      this.syncHeaderScrollbarWidth();
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
      if (this.summaryContainer) {
        const summaryEl = this.summaryContainer;
        summaryEl.style.paddingRight = `${scrollbarWidth}px`;

        const summaryRow = summaryEl.querySelector('.routes-table-summary-row');
        if (summaryRow) {
          const summaryWidth = summaryRow.scrollWidth;
          const innerWrappers = [
            headerEl.querySelector('.table-inner-wrapper'),
            bodyEl.querySelector('.table-inner-wrapper')
          ];
          innerWrappers.forEach(wrapper => {
            if (wrapper) {
              wrapper.style.minWidth = `${summaryWidth}px`;
            }
          });
        }
      }
    }
  }

  ngOnDestroy() {
    this.vmSubscription?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedRouteId && !changes.selectedRouteId.currentValue && !changes.selectedRouteId.firstChange) {
      setTimeout(() => {
        this.viewport?.checkViewportSize();
        this.cdr.detectChanges();
      }, 0);
    }
    if (changes.panels || changes.resourcesDropListIds || changes.routesDropListIds) {
      const drilldownLists = [];
      const filteredPanels = _.filter(this.panels, (p) => {
        if (p.id === this.panel.id) {
          return false;
        }
        drilldownLists.push(`${p.id}-drilldown`);
        return true;
      });
      this.allPanelIds = _.concat(
        _.map(filteredPanels, 'id'),
        this.resourcesDropListIds || [],
        this.routesDropListIds || [],
        drilldownLists
      );
    }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      if (this.selectedIds) {
        _.forEach(this.selectedIds, (id) => {
          this.selectedKeys[id] = true;
        });
      }
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      if (this.visibleIds) {
        _.forEach(this.visibleIds, (id) => {
          this.visibleKeys[id] = true;
        });
      }
      this.cdr.detectChanges();
    }
    const idsContentChanged = !!changes.ids && !_.isEqual(changes.ids.currentValue, changes.ids.previousValue);

    if (
      changes.queryString ||
      idsContentChanged ||
      changes.from ||
      changes.to ||
      changes.status ||
      changes.branchIds ||
      changes.depotIds ||
      changes.areaIds ||
      changes.agentIds ||
      changes.agentSkills ||
      changes.resourceIds ||
      changes.agentScheduleIds ||
      changes.tags ||
      changes.resourceTags ||
      changes.vehicleTypeIds ||
      changes.relationTypes ||
      changes.sortBy ||
      changes.sortType
    ) {
      this._resetStore();
      if (!(this.isResourceView && Array.isArray(this.ids) && !this.ids.length)) {
        this._getRoutes();
      }
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.viewport?.checkViewportSize();
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const isVerticalScroll = target.scrollTop !== this.lastScrollTop;
    this.lastScrollTop = target.scrollTop;

    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
    if (this.summaryContainer) {
      const summaryEl = this.summaryContainer;
      summaryEl.scrollLeft = target.scrollLeft;
    }

    if (isVerticalScroll) {
      this.getNextBatch(event);
    }
  }

  getNextBatch(event: Event) {
    const nextBatchThreshold = this.viewport.getViewportSize();
    if (
      !this.vm.loading &&
      this.viewport.measureScrollOffset('bottom') < nextBatchThreshold &&
      !!this.vm.cursor
    ) {
      this._getRoutes();
    }
    this.viewport.checkViewportSize();
  }

  onScroll(_: Event): void {
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
      }
      this.scrollEndTimer = null;
    }, 200);
  }

  cdkEnterPredicate(): boolean {
    return this.isResourceView;
  }

  onListDrop(event: CdkDragDrop<any>) {
    if (this.isResourceView) {
      this.onRoutesDropped.emit(event);
    }
  }

  onToggleSelectedRoute(route: RouteModel, isSelected: boolean) {
    this.selectedKeys[route.id] = isSelected;
    this.onToggleSelectRoute.emit({route: route, isSelected: isSelected});
  }

  onToggleLocked(route: RouteModel) {
    this.onLockRouteChange.emit({id: route.id, locked: !route.isBlockedByUser});
  }

  onDrillDownClick(route: RouteModel, event: any) {
    event.stopPropagation();
    this.onDrillDown.emit({routeId: route.id, event: event});
  }

  onRowClick(route: RouteModel, event: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.clickTimeout = setTimeout(() => {
      this.onSelect.emit(route);
      this.clickTimeout = null;
    }, 250);
  }

  onDrillDownDblClick(route: RouteModel, event: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    event.stopPropagation();
    this.onDrillDown.emit({routeId: route.id, event: event});
  }

  private _resetStore() {
    this.routesStore.resetRoutes();
  }

  private _getRoutes() {
    this.routesStore.getRoutes(this._getRoutesQuery());
  }

  private _getRoutesQuery(): RoutesQueryModel {
    const resourceIds = this.resourceIds || [];
    const agentScheduleIds = this.agentScheduleIds || [];

    const query = {
      ids: this.ids,
      expandServices: false,
      expandTasks: false,
      expandTasksExt: false,
      query: this.queryString,
      from: this.from,
      to: this.to,
      status: this.status,
      branchIds: this.branchIds,
      depotIds: this.depotIds,
      areaIds: this.areaIds,
      agentIds: this.agentIds,
      skill: this.agentSkills,
      allocationIds: resourceIds.concat(agentScheduleIds),
      tags: this.tags,
      resourceTags: this.resourceTags,
      vehicleTypeIds: this.vehicleTypeIds,
      relationTypes: this.relationTypes,
      limit: this.loadLimit,
      sort: (this.sortBy && this.sortType) ? `${this.sortBy},${this.sortType}` : undefined
    };

    Object.keys(query).forEach(key => query[key] === undefined ? delete query[key] : {});
    return query;
  }

  public trackById(_: number, item: RouteModel): string {
    return item.id;
  }

  protected readonly routeStatusType = RouteStatusEnum;
}
